import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { selectUserToken } from "../../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { ReceiptsEndPointLogic } from "../../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import { useDispatch } from "react-redux";
import {
  addFmeToReceipt,
  removeAllFmesForReceipt,
  removeFmeFromReceipt,
  setActiveReceiptSharedUid,
  setFinancialMonthlyEarningsData,
  setFMEIdPair,
  setFMERecordsLoading,
  setSelectedCompanyToBrowseFME,
  setSelectedDateFilterPair,
  setSelectedFinanceCompanyFilter,
  setSelectedLinkedFilterPair,
  setSelectedStatusFilterPair,
} from "../../../../../../features/financeReceiptSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReceiptStatus } from "../../ReceiptsTable/ReceiptStatus";
import {
  calculateCoveredPercentage,
  calculateCoveredPercentageForFme,
  calculateDebitForFme,
  calculateDebitForReceipt,
} from "../../helpers/funcs/calculateDebitAndCPAndMismatchLogicForReceipt";
import FMEChip from "../../helpers/components/FMEChip";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
}
const PairingRecordsChosenTable = ({ tableHeadings, rows }: Props) => {
  const [orderBy, setOrderBy] = React.useState<string | null>(null);
  const [order, setOrder] = React.useState<"asc" | "desc">("asc");
  const headCells = Object.entries(tableHeadings);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompanyToBrowseFME, linkedFmesByReceipt } = useSelector(
    (state: any) => state.receipts,
  );

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [, setPendingDeleteRow] = React.useState<any>(null);
  const receiptSharedUIDs =
    searchParams.get("receipt_shared_uid")?.split(",") ?? [];

  const dispatch = useDispatch();
  // const handleChangePage = (_: any, newPage: number) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (
  //   event: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   setRowsPerPage(+event.target.value);
  //   setPage(0);
  // };

  const handleSort = (key: string) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrder("asc");
    }
  };

  const handleDeleteFmeLocal = (
    receiptSharedUid: string,
    fmeSharedUid: string,
  ) => {
    dispatch(
      removeFmeFromReceipt({
        receiptSharedUid,
        fmeSharedUid,
      }),
    );
  };
  // const handleDeleteFmeWithApi = async (
  //   receiptSharedUid: string,
  //   fmeSharedUid: string,
  // ) => {
  //   try {
  //     setUnPairingRecords(true);
  //     const payload = {
  //       receipt_shared_uid: receiptSharedUid,
  //       fme_shared_uids: [fmeSharedUid],
  //     };
  //     await ReceiptsEndPointLogic.deleteFinancialMonthlyEarnings(
  //       oauthTokenHeader,
  //       payload,
  //     );

  //     // Only after success
  //     handleDeleteFmeLocal(receiptSharedUid, fmeSharedUid);
  //   } catch (error) {
  //     console.error("Failed to delete FME", error);
  //   } finally {
  //     setUnPairingRecords(false);
  //   }
  // };

  const handleDelete = (receiptUid: string, fme: any) => {
    handleDeleteFmeLocal(receiptUid, fme.shared_uid);
  };

  const handleAddRecords = async (row: any) => {
    dispatch(setSelectedDateFilterPair(null));
    dispatch(setSelectedStatusFilterPair(null));
    dispatch(setSelectedLinkedFilterPair(null));
    dispatch(setFMEIdPair(null));
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("company_shared_uid", row.company_shared_uid);

      params.delete("fme");
      //params.delete("filters");
      return params;
    });
    dispatch(setActiveReceiptSharedUid(row.receipt_shared_uid));
    dispatch(setSelectedCompanyToBrowseFME(row.finance_company_name));
    dispatch(setSelectedFinanceCompanyFilter([row.company_shared_uid]));
    dispatch(setFMERecordsLoading(true));
    try {
      await Promise.all([
        ReceiptsEndPointLogic.getFinancialMonthlyEarnings(
          oauthTokenHeader,
          row.company_shared_uid,
        ),
        ReceiptsEndPointLogic.getAllCurrencies(oauthTokenHeader),
      ]);
    } catch (error) {
      console.error("Failed to fetch FME records", error);
    } finally {
      dispatch(setFMERecordsLoading(false));
    }
  };

  const handleDeleteRow = (row: any) => {
    const isDeletingActiveRecord =
      row.finance_company_name === selectedCompanyToBrowseFME;
    // Case 1: only one receipt left → confirm
    if (receiptSharedUIDs.length === 1) {
      setPendingDeleteRow(row);
      setConfirmOpen(true);
      return;
    }
    // Case 2: normal delete
    const updatedUIDs = receiptSharedUIDs.filter(
      (uid) => uid !== row.receipt_shared_uid,
    );
    const params = new URLSearchParams(searchParams);
    params.set("receipt_shared_uid", updatedUIDs.join(","));
    setSearchParams(params, { replace: true });

    dispatch(removeAllFmesForReceipt(row.receipt_shared_uid));

    if (isDeletingActiveRecord) {
      dispatch(setSelectedCompanyToBrowseFME(null));
      dispatch(setFinancialMonthlyEarningsData([]));
    }
  };

  const handleRedirectBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("receipt_shared_uid");
    params.delete("fme");

    navigate({
      pathname: "/finance/receipts",
      search: params.toString(),
    });
  };

  const sortedRows = React.useMemo(() => {
    if (!orderBy) return rows;

    return [...rows].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (orderBy === "debit" || orderBy === "covered") {
        const aFmes = linkedFmesByReceipt[a.receipt_shared_uid] || [];
        const bFmes = linkedFmesByReceipt[b.receipt_shared_uid] || [];

        aVal =
          orderBy === "debit"
            ? calculateDebitForReceipt(aFmes)
            : calculateCoveredPercentage(
                calculateDebitForReceipt(aFmes),
                a.receipt_amount,
              );

        bVal =
          orderBy === "debit"
            ? calculateDebitForReceipt(bFmes)
            : calculateCoveredPercentage(
                calculateDebitForReceipt(bFmes),
                b.receipt_amount,
              );
      }

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return order === "asc" ? aVal - bVal : bVal - aVal;
      }
      if (orderBy === "receipt_date") {
        return order === "asc"
          ? new Date(aVal).getTime() - new Date(bVal).getTime()
          : new Date(bVal).getTime() - new Date(aVal).getTime();
      }
      return order === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [rows, orderBy, order, linkedFmesByReceipt]);

  React.useEffect(() => {
    rows.forEach((row) => {
      const alreadyHydrated =
        linkedFmesByReceipt[row.receipt_shared_uid] !== undefined;

      if (alreadyHydrated) return;
      if (!row.fmes || row.fmes.length === 0) return;

      row.fmes.forEach((fme: any) => {
        dispatch(
          addFmeToReceipt({
            receiptSharedUid: row.receipt_shared_uid,
            fme: {
              id: fme.fme_id,
              shared_uid: fme.fme_shared_uid,
              isPrefilled: true, // 🔴 CRITICAL
              ...fme,
            },
          }),
        );
      });
    });
  }, [rows, dispatch, linkedFmesByReceipt]);

  return (
    <>
      <Paper sx={{ py: 1 }}>
        <Paper sx={{ mt: 0, boxShadow: "none" }}>
          <TableContainer
            className="receiptsTable"
            //  component={Paper}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map(([key, value], index) => (
                    <TableCell key={index} align="left">
                      <TableSortLabel
                        active={orderBy === key}
                        direction={orderBy === key ? order : "asc"}
                        onClick={() => handleSort(key)}
                      >
                        {value}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => {
                  const rawFmes =
                    linkedFmesByReceipt[row.receipt_shared_uid] || [];
                  const validFmes = rawFmes.filter(
                    (fme: any) => fme?.shared_uid && fme?.id,
                  );

                  return (
                    <React.Fragment key={row.cron_id}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                          background: "rgba(245, 245, 245, 1)",
                          "&:hover": {
                            backgroundColor: "#ffffff",
                            border: "1px solid #9c27b0 !important",
                          },
                        }}
                      >
                        {headCells.map(([key], cellIndex) => (
                          <TableCell key={cellIndex} align="left">
                            {key === "finance_company_name" ? (
                              <>
                                <IconButton
                                  aria-label="delete"
                                  size="small"
                                  sx={{ mr: 1 }}
                                  onClick={() => handleDeleteRow(row)}
                                >
                                  <CloseIcon />
                                </IconButton>

                                {row[key] || "N/A"}
                              </>
                            ) : key === "receipt_date" ? (
                              <> {row[key] ? row[key] : "N/A"}</>
                            ) : key === "receipt_id" ? (
                              <> {row[key] ? row[key] : "N/A"}</>
                            ) : key === "receipt_amount" ? (
                              <>
                                <Box display="flex" alignItems="center">
                                  {row[key] ?? "N/A"}
                                  <Typography
                                    sx={{
                                      ml: 1,
                                      color: "rgba(0, 0, 0, 0.56)",
                                      fontSize: "18px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {row?.receipt_currency_symbol ?? ""}
                                  </Typography>
                                </Box>
                              </>
                            ) : key === "receipt_status" ? (
                              <>
                                <ReceiptStatus status={row?.receipt_status} />
                              </>
                            ) : key === "fme" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                    overflowX:
                                      validFmes.length >= 3 ? "auto" : "hidden",
                                    maxWidth: "500px",
                                    paddingRight:
                                      validFmes.length >= 3 ? "8px" : "0",
                                    scrollBehavior: "smooth",
                                    "&::-webkit-scrollbar": {
                                      height: "5px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                      backgroundColor: "transparent",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                      backgroundColor: "#c4c3c3ff",
                                      borderRadius: "3px",
                                      "&:hover": {
                                        backgroundColor: "#a3a3a3ff",
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      flexWrap: "nowrap",
                                      minWidth: "fit-content",
                                      paddingBottom: "5px",
                                    }}
                                  >
                                    {validFmes.map((fme: any) => {
                                      const debit = calculateDebitForFme(fme);
                                      const covered =
                                        calculateCoveredPercentageForFme(
                                          debit,
                                          row.receipt_amount,
                                        );

                                      return (
                                        <FMEChip
                                          key={fme.shared_uid}
                                          fmeId={fme.id}
                                          debit={debit}
                                          covered={covered}
                                          currencySymbol={
                                            row.receipt_currency_symbol
                                          }
                                          onDelete={() =>
                                            handleDelete(
                                              row.receipt_shared_uid,
                                              fme,
                                            )
                                          }
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                                {validFmes.length === 0 ? (
                                  <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddRecords(row)}
                                    sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                    data-tracker="586"
                                  >
                                    Click to add records
                                  </Button>
                                ) : (
                                  <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddRecords(row)}
                                    sx={{ flexShrink: 0 }}
                                    data-tracker="586"
                                  />
                                )}
                                {/* Always show Add button */}
                              </Box>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(rows?.length / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack> */}
        </Paper>
      </Paper>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remove all receipts?</DialogTitle>
        <DialogContent>
          <Typography>
            You are removing all the receipts. You will be redirected back to
            the receipt view page.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ color: "#9c27b0" }}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              handleRedirectBack();
            }}
          >
            Continue anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PairingRecordsChosenTable;
